import { Scroll, Stack } from '@/components/layout';
import { STRINGS } from '@/constants/strings';
import {
  ProfileContentPlaceholder,
  ProfileEditPanel,
  ProfileHeader,
  ProfilePanel,
  REPORT_ISSUE_SETTING_KEY,
} from '@/features/guardian-profile/components';
import { useGuardianProfile } from '@/features/guardian-profile/hooks';
import { GuardianHelpAndSupportContent } from '@/features/guardian-profile/screens/guardian-help-and-support-screen';
import { GuardianNotificationPreferencesContent } from '@/features/guardian-profile/screens/guardian-notification-preferences-screen';
import type { ProfileAction } from '@/features/guardian-profile/types';
import { createStyles, useGuardianStackNavigation, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import React, { useCallback, useMemo, useState } from 'react';
import { RefreshControl, StatusBar } from 'react-native';
import LinearGradient from 'react-native-linear-gradient';
import { GuardianAccountSecuritiesContent } from './guardian-account-securities-screen';
import { GuardianPrivacyLegalContent } from './guardian-privacy-legal-screen';

export function GuardianProfileScreen() {
  const styles = useStyles();
  const { isTablet } = useScaling();
  const navigation = useGuardianStackNavigation();
  const { name, avatarUri, coGuardians, requestLogout, refetch, isRefetching } =
    useGuardianProfile();
  const [selectedSetting, setSelectedSetting] = useState<ProfileAction>({
    key: 'account',
    label: STRINGS.guardianProfile.settings.account,
    icon: 'IcUserSquare',
  });

  const handleSelectSetting = useCallback(
    (action: ProfileAction) => {
      // "Report an issue" opens its own sheet from the panel — it has no screen/tablet panel,
      // so don't route to it or swap the tablet detail pane.
      if (action.key === REPORT_ISSUE_SETTING_KEY) return;

      if (isTablet) {
        setSelectedSetting(action);
        return;
      }

      switch (action.key) {
        case 'security':
          navigation.navigate('GuardianAccountSecurities');
          break;
        case 'notifications':
          navigation.navigate('GuardianNotificationPreferences', { source: undefined });
          break;
        case 'support':
          navigation.navigate('GuardianHelpAndSupport');
          break;
        case 'legal':
          navigation.navigate('GuardianPrivacyLegal');
          break;
        default:
          break;
      }
    },
    [isTablet, navigation],
  );
  const selectedPanel = useMemo(() => {
    switch (selectedSetting.key) {
      case 'account':
        return <ProfileEditPanel />;
      case 'security':
        return <GuardianAccountSecuritiesContent tabletPanel />;
      case 'notifications':
        return (
          <GuardianNotificationPreferencesContent
            tabletPanel
            onSaved={() =>
              setSelectedSetting({
                key: 'account',
                label: STRINGS.guardianProfile.settings.account,
                icon: 'IcUserSquare',
              })
            }
          />
        );
      case 'support':
        return <GuardianHelpAndSupportContent tabletPanel />;
      case 'legal':
        return <GuardianPrivacyLegalContent tabletPanel />;
      default:
        return <ProfileContentPlaceholder title={selectedSetting.label} />;
    }
  }, [selectedSetting.key, selectedSetting.label]);

  return (
    <Stack flex={1} backgroundColor={guardianColors['1']}>
      <StatusBar barStyle="dark-content" backgroundColor={isTablet ? colors['0'] : '#EEF3FF'} />
      {isTablet ? (
        <>
          <ProfileHeader onLogout={requestLogout} />
          <Stack flex={1} direction="row">
            <Scroll
              style={styles.tabletPanelScroll}
              contentContainerStyle={styles.tabletContent}
              showsVerticalScrollIndicator={false}
              refreshControl={<RefreshControl refreshing={isRefetching} onRefresh={refetch} />}>
              <ProfilePanel
                tablet
                name={name}
                avatarUri={avatarUri}
                coGuardians={coGuardians}
                onLogout={requestLogout}
                selectedSettingKey={selectedSetting.key}
                onSelectSetting={handleSelectSetting}
              />
            </Scroll>
            <Stack style={styles.tabletDivider} />
            {selectedPanel}
          </Stack>
        </>
      ) : (
        <LinearGradient
          colors={['#EEF3FF', guardianColors['1'], '#EEF3FF']}
          locations={[0.08, 0.47, 0.9]}
          useAngle
          angle={218.09}
          style={styles.gradient}>
          <ProfileHeader
            onLogout={requestLogout}
            onEdit={() => navigation.navigate('GuardianProfileEdit')}
          />
          <Scroll
            style={styles.scroll}
            contentContainerStyle={styles.phoneContent}
            showsVerticalScrollIndicator={false}
            refreshControl={<RefreshControl refreshing={isRefetching} onRefresh={refetch} />}>
            <ProfilePanel
              name={name}
              avatarUri={avatarUri}
              coGuardians={coGuardians}
              onLogout={requestLogout}
              onSelectSetting={handleSelectSetting}
            />
          </Scroll>
        </LinearGradient>
      )}
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  gradient: { flex: 1 },
  scroll: { flex: 1 },
  phoneContent: { paddingHorizontal: scale(16), paddingTop: scale(4), paddingBottom: scale(32) },
  tabletContent: {
    flexGrow: 1,
    alignItems: 'flex-start',
    paddingTop: scale(16),
    paddingLeft: scale(18),
    paddingRight: scale(19),
    paddingBottom: scale(100),
  },
  tabletPanelScroll: { width: scale(375), flexGrow: 0 },
  tabletDivider: {
    width: 1,
    marginVertical: scale(16),
    backgroundColor: guardianColors['3'],
  },
}));
